import { useAuthStore } from "@/stores/auth.store";
import { signIn, signUp, sendPasswordResetEmail, submitNewPassword } from "supertokens-web-js/recipe/emailpassword";
import Session from "supertokens-web-js/recipe/session";

export function useAuth() {
    const authStore = useAuthStore();
    const loading = ref(false);
    const error = ref(null);


    async function login(loginForm) {
        loading.value = true;
        error.value = null;

        const { email, password } = unref(loginForm);

        try {
            const response = await signIn({
                formFields: [
                    { id: "email", value: email },
                    { id: "password", value: password },
                ],
            });

            if (response.status === "OK") {
                await authStore.checkAuth();
                return true;
            } else {
                throw new Error("Login failed");
            }
        } catch (err) {
            error.value = err.message
            return false;
        } finally {
            loading.value = false;
        }
    }

    async function register(registerForm) {
        loading.value = true;
        error.value = null;

        const { accountForm, companyForm, missionForm } = unref(registerForm);

        try {
            const response = await signUp({
                formFields: [
                    { id: "email", value: accountForm.email },
                    { id: "password", value: accountForm.password },
                    { id: "accountForm", value: accountForm },
                    { id: "companyForm", value: companyForm },
                    { id: "missionForm", value: missionForm },
                ],
            });

            if (response.status === "OK") {
                await authStore.checkAuth();
                return true;
            } else if (response.status === "FIELD_ERROR") {
                // one of the input formFields failed validation
                let registerToast;

                const emailErrorField = response.formFields.find(x => x.id === "email");

                // Gestion erreurs liées au mail
                if (emailErrorField.error === "This email already exists. Please sign in instead.") {
                    registerToast = toast.error("L'adresse email saisie est déjà présente dans notre base. Essayer de vous connecter");
                    return false;
                } else if (emailErrorField.error === "Email is invalid") {
                    registerToast = toast.error("L'adresse email saisie n'est pas valide");
                    return false;
                }

                registerToast.error("Certains champs saisis ne sont pas correct");
                return false;
            } else {
                throw new Error("Registration failed");
            }
        } catch (err) {
            error.value = err.message;
            return false;
        } finally {
            loading.value = false;
        }
    }

    async function logout() {
        loading.value = true;
        error.value = null;

        try {
            await Session.signOut();
            await authStore.clearUser();
            return true;
        } catch (err) {
            error.value = err.message;
            return false;
        } finally {
            loading.value = false;
        }
    }

    function setupAuth() {
        // Setup cookie listener for auth state changes
        // Initial auth check is handled by the router guard
        const { addChangeListener } = useCookies();

        addChangeListener((cookie) => {
            if (cookie.name !== "sFrontToken")
                return;

            authStore.checkAuth();
        });
    }

    async function forgotPassword(email) {
        loading.value = true;
        error.value = null;

        try {
            const response = await sendPasswordResetEmail({
                formFields: [
                    { id: "email", value: email },
                ],
            });

            if (response.status === "OK") {
                return { success: true };
            } else if (response.status === "FIELD_ERROR") {
                const emailErrorField = response.formFields.find(x => x.id === "email");
                error.value = emailErrorField?.error || "Invalid email";
                return { success: false, error: error.value };
            } else {
                throw new Error("Password reset failed");
            }
        } catch (err) {
            error.value = err.message;
            return { success: false, error: err.message };
        } finally {
            loading.value = false;
        }
    }

    async function resetPassword(password, token, tenantId) {
        loading.value = true;
        error.value = null;

        try {
            const response = await submitNewPassword({
                formFields: [
                    { id: "password", value: password },
                ],
                options: {
                    preAPIHook: (context) => {
                        // Set the token and tenantId from URL parameters
                        context.url += `?token=${encodeURIComponent(token)}`;
                        if (tenantId) {
                            context.url += `&tenantId=${encodeURIComponent(tenantId)}`;
                        }
                        return context;
                    }
                }
            });

            if (response.status === "OK") {
                return { success: true };
            } else if (response.status === "RESET_PASSWORD_INVALID_TOKEN_ERROR") {
                error.value = "Reset token expired";
                return { success: false, error: "RESET_PASSWORD_INVALID_TOKEN_ERROR" };
            } else if (response.status === "FIELD_ERROR") {
                const passwordErrorField = response.formFields.find(x => x.id === "password");
                error.value = passwordErrorField?.error || "Invalid password";
                return { success: false, error: "FIELD_ERROR" };
            } else {
                throw new Error("Password reset failed");
            }
        } catch (err) {
            error.value = err.message;
            return { success: false, error: err.message };
        } finally {
            loading.value = false;
        }
    }

    return {
        authStore,
        setupAuth,
        login,
        register,
        logout,
        forgotPassword,
        resetPassword,
        loading,
        error,
    };
}
